import { useState, useCallback, FormEvent, ChangeEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../store';
import { useTranslation } from '../hooks/useTranslation';
import { VALIDATION } from '@spellion/shared';
import { PasswordInput } from '../components/common/PasswordInput';

/**
 * RegisterPage Component
 *
 * User registration page for creating new accounts.
 * Features:
 * - Email and password fields with real-time validation
 * - Password requirements display with visual feedback
 * - Error handling for already registered email with password recovery link
 * - Terms acceptance checkbox
 * - Login link for existing users
 *
 * @validates Requirements 6.1, 6.3, 6.4, 6.5, 6.8, 6.9
 */

/** API base URL from environment */
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/** Email validation error types */
type EmailErrorType = 'required' | 'atSign' | 'domain' | 'invalid' | null;

/** Email validation: exactly one "@" followed by a domain with at least one "." */
function validateEmail(email: string): { valid: boolean; errorType: EmailErrorType } {
  if (!email) {
    return { valid: false, errorType: null };
  }

  const atCount = (email.match(/@/g) || []).length;
  if (atCount !== 1) {
    return {
      valid: false,
      errorType: 'atSign',
    };
  }

  const [, domain] = email.split('@');
  if (!domain || !domain.includes('.')) {
    return {
      valid: false,
      errorType: 'domain',
    };
  }

  // Basic format check
  if (!VALIDATION.EMAIL_REGEX.test(email)) {
    return {
      valid: false,
      errorType: 'invalid',
    };
  }

  return { valid: true, errorType: null };
}

/** Password validation criteria */
interface PasswordCriteria {
  minLength: boolean;
  maxLength: boolean;
  hasLetter: boolean;
  hasNumber: boolean;
  hasUppercase: boolean;
  hasSpecial: boolean;
}

/** Check password against requirements */
function validatePassword(password: string): {
  valid: boolean;
  criteria: PasswordCriteria;
} {
  const criteria: PasswordCriteria = {
    minLength: password.length >= VALIDATION.PASSWORD_MIN_LENGTH,
    maxLength: password.length <= VALIDATION.PASSWORD_MAX_LENGTH,
    hasLetter: /[a-zA-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password),
  };

  const valid =
    criteria.minLength &&
    criteria.maxLength &&
    criteria.hasLetter &&
    criteria.hasNumber &&
    criteria.hasUppercase &&
    criteria.hasSpecial;

  return { valid, criteria };
}

/** Error codes from API */
const ERROR_CODES = {
  EMAIL_EXISTS: 'AUTH_EMAIL_EXISTS',
  INVALID_EMAIL: 'INVALID_EMAIL_FORMAT',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
} as const;

export function RegisterPage() {
  const { setLoading, setError, isLoading, error, clearError } =
    useStore();
  const { t } = useTranslation();
  
  // Config state from store
  const freeUserDailyQuota = useStore((state) => state.freeUserDailyQuota);
  const loadPublicConfig = useStore((state) => state.loadPublicConfig);

  // Load public config on mount
  useEffect(() => {
    loadPublicConfig();
  }, [loadPublicConfig]);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Validation state
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  // Email already exists error with recovery suggestion
  const [emailExistsError, setEmailExistsError] = useState(false);
  
  // Registration success state - email verification required
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Validate email
  const emailValidation = validateEmail(email);
  const showEmailError = emailTouched && !emailValidation.valid && email.length > 0;

  // Get email error message based on error type
  const getEmailErrorMessage = (): string => {
    switch (emailValidation.errorType) {
      case 'required':
        return t('register.email.error.required');
      case 'atSign':
        return t('register.email.error.atSign');
      case 'domain':
        return t('register.email.error.domain');
      case 'invalid':
        return t('register.email.error.invalid');
      default:
        return '';
    }
  };

  // Validate password
  const passwordValidation = validatePassword(password);
  const showPasswordError = passwordTouched && !passwordValidation.valid && password.length > 0;

  // Validate confirm password
  const passwordsMatch = password === confirmPassword;
  const showConfirmPasswordError =
    confirmPasswordTouched && !passwordsMatch && confirmPassword.length > 0;

  // Form is valid when all fields are valid
  const isFormValid =
    emailValidation.valid &&
    passwordValidation.valid &&
    passwordsMatch &&
    termsAccepted;

  // Handle email change
  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setEmailExistsError(false);
      clearError();
    },
    [clearError]
  );

  // Handle email blur
  const handleEmailBlur = useCallback(() => {
    setEmailTouched(true);
  }, []);

  // Handle password change
  const handlePasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
      clearError();
    },
    [clearError]
  );

  // Handle password blur
  const handlePasswordBlur = useCallback(() => {
    setPasswordTouched(true);
  }, []);

  // Handle confirm password change
  const handleConfirmPasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setConfirmPassword(e.target.value);
    },
    []
  );

  // Handle confirm password blur
  const handleConfirmPasswordBlur = useCallback(() => {
    setConfirmPasswordTouched(true);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Mark all fields as touched
      setEmailTouched(true);
      setPasswordTouched(true);
      setConfirmPasswordTouched(true);

      if (!isFormValid) {
        return;
      }

      setLoading(true);
      setError(null);
      setEmailExistsError(false);

      try {
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (!response.ok) {
          // Handle specific error codes
          if (data.code === ERROR_CODES.EMAIL_EXISTS) {
            setEmailExistsError(true);
            setError(null);
          } else if (data.code === ERROR_CODES.INVALID_EMAIL) {
            setError(t('register.errors.invalidEmail'));
          } else if (data.code === ERROR_CODES.INVALID_PASSWORD) {
            setError(t('register.errors.invalidPassword'));
          } else {
            setError(data.message || t('register.errors.generic'));
          }
          return;
        }

        // Registration successful - email verification required
        // Backend no longer returns token on registration (requires email verification)
        setRegistrationSuccess(true);
        setRegisteredEmail(email);
      } catch (err) {
        console.error('Registration error:', err);
        setError(t('register.errors.network'));
      } finally {
        setLoading(false);
      }
    },
    [email, password, isFormValid, setLoading, setError]
  );

  // If registration was successful, show verification email sent message
  if (registrationSuccess) {
    return (
      <div className="max-w-md mx-auto py-8">
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-green-600" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" 
              />
            </svg>
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
            {t('register.success.title')}
          </h1>
          <p className="text-slate-600 mb-6">
            {t('register.success.message')}{' '}
            <span className="font-semibold text-slate-900">{registeredEmail}</span>.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            {t('register.success.instruction')}
          </p>
          <Link
            to="/login"
            className="inline-block w-full btn-primary"
          >
            {t('register.success.goToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8">
      <div className="text-center space-y-2 mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900">
          {t('register.title')}
        </h1>
        <p className="text-slate-600">
          {t('register.subtitle')}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-100"
        noValidate
      >
        {/* Global error message */}
        {error && (
          <div
            className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Email field */}
        <div className="space-y-2">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-slate-700"
          >
            {t('register.email.label')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            placeholder={t('register.email.placeholder')}
            className={`input ${
              showEmailError || emailExistsError
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : emailTouched && emailValidation.valid
                ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                : ''
            }`}
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={isLoading}
            aria-invalid={showEmailError || emailExistsError}
            aria-describedby={
              showEmailError || emailExistsError ? 'email-error' : undefined
            }
            required
          />
          {/* Email format error - Requirement 6.8 */}
          {showEmailError && (
            <p id="email-error" className="text-sm text-red-600" role="alert">
              {getEmailErrorMessage()}
            </p>
          )}
          {/* Email already exists error - Requirement 6.5 */}
          {emailExistsError && (
            <div
              id="email-error"
              className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
              role="alert"
            >
              <p className="text-sm text-amber-800">
                {t('register.errors.emailExists')}{' '}
                <Link
                  to="/password-reset"
                  className="font-medium text-primary-600 hover:text-primary-700 underline"
                >
                  {t('register.errors.emailExistsRecovery')}
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Password field */}
        <div className="space-y-2">
          <label
            htmlFor="password"
            className="block text-sm font-medium text-slate-700"
          >
            {t('register.password.label')}
          </label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="new-password"
            placeholder={t('register.password.placeholder')}
            hasError={showPasswordError}
            hasSuccess={passwordTouched && passwordValidation.valid}
            value={password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            disabled={isLoading}
            aria-invalid={showPasswordError}
            aria-describedby="password-requirements"
            required
          />
          {/* Password requirements - Simple text like Budgira */}
          <p
            id="password-requirements"
            className="text-xs text-slate-500 mt-2"
          >
            {t('register.password.requirements.hint')}
          </p>
          {/* Password error message - Requirement 6.9 */}
          {showPasswordError && !passwordValidation.valid && (
            <p className="text-sm text-red-600" role="alert">
              {t('register.password.error.notMeetCriteria')}
            </p>
          )}
        </div>

        {/* Confirm password field */}
        <div className="space-y-2">
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-slate-700"
          >
            {t('register.confirmPassword.label')}
          </label>
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            placeholder={t('register.confirmPassword.placeholder')}
            hasError={showConfirmPasswordError}
            hasSuccess={confirmPasswordTouched && passwordsMatch && confirmPassword.length > 0}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            onBlur={handleConfirmPasswordBlur}
            disabled={isLoading}
            aria-invalid={showConfirmPasswordError}
            aria-describedby={
              showConfirmPasswordError ? 'confirm-password-error' : undefined
            }
            required
          />
          {showConfirmPasswordError && (
            <p
              id="confirm-password-error"
              className="text-sm text-red-600"
              role="alert"
            >
              {t('register.confirmPassword.error.noMatch')}
            </p>
          )}
        </div>

        {/* Terms acceptance */}
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="terms"
            name="terms"
            className="w-4 h-4 mt-0.5 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            disabled={isLoading}
            required
          />
          <label htmlFor="terms" className="text-sm text-slate-600">
            {t('register.terms.accept')}{' '}
            <Link to="/terms" className="text-primary-600 hover:underline">
              {t('register.terms.termsOfService')}
            </Link>{' '}
            {t('register.terms.and')}{' '}
            <Link to="/privacy" className="text-primary-600 hover:underline">
              {t('register.terms.privacyPolicy')}
            </Link>
          </label>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          className="w-full btn-primary"
          disabled={isLoading || !isFormValid}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner />
              {t('register.submitting')}
            </span>
          ) : (
            t('register.submit')
          )}
        </button>

        {/* Free tier info */}
        <div className="text-center p-4 bg-primary-50 rounded-xl">
          <p className="text-sm text-primary-800">
            <strong>{t('register.freeAccount').replace('{count}', String(freeUserDailyQuota))}</strong>
          </p>
        </div>
      </form>

      {/* Login link */}
      <p className="text-center mt-6 text-slate-600">
        {t('register.haveAccount')}{' '}
        <Link
          to="/login"
          className="font-medium text-primary-600 hover:text-primary-700"
        >
          {t('register.login')}
        </Link>
      </p>
    </div>
  );
}

/**
 * Password criteria item component
 * Shows a checkmark or bullet based on whether the criteria is met
 */
/**
 * Simple loading spinner component
 */
function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}
